import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getLocationName } from './constants';
import { 
  getAuthToken, 
  getTransactionsByDate, 
  getUsersByDate, 
  getLateTransactions,
  refundLateFee,
  searchCustomers,
  markTransactionReturned
} from './service';

import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
// import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
// import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InfoIcon from '@mui/icons-material/Info';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';

const BRAND_GREEN = '#36b63f';
const BRAND_GREEN_DARK = '#2a8f32';
const BRAND_GREEN_LIGHT = '#e8f5e9';
const BRAND_GREEN_BORDER = '#c8e6c9';

const todayISODate = () => new Intl.DateTimeFormat('en-CA').format(new Date());

const HeaderCell = ({ children }) => (
  <TableCell sx={{ fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>
    {children}
  </TableCell>
);

const GracePeriodNote = () => (
  <Alert 
    icon={<InfoIcon fontSize="inherit" />} 
    severity="info" 
    sx={{ 
      mb: 3, 
      backgroundColor: '#f0f7ff', 
      color: '#1e40af', 
      border: '1px solid #dbeafe'
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      <strong>Extended Return Window:</strong> If a user has multiple reusables checked out, we automatically extend the due date of older items to match the latest due date. This prevents accidental late fees.
    </Typography>
  </Alert>
);

const StatusBadge = ({ status, lateFeeCharged, paymentStatus, refunded }) => {
  if (refunded) return <Chip label="Refunded" size="small" color="secondary" sx={{ fontWeight: 600 }} />;

  let color = 'default';
  let label = status || 'Unknown';

  if (paymentStatus?.toLowerCase() === 'failed') {
    color = 'error';
    label = 'Late - Payment failure';
  } else if (lateFeeCharged) {
    color = 'error';
    label = 'Late Fee Charged';
  } else {
    switch (status?.toLowerCase()) {
      case 'returned': color = 'success'; break;
      case 'late': color = 'error'; break;
      case 'not returned': case 'unreturned': color = 'warning'; break;
      case 'partially returned': color = 'info'; break;
      case 'grace period': color = 'info'; label = 'Grace Period'; break;
      case 'extended return window': color = 'info'; label = 'Extended Return Window'; break;
      default: color = 'default';
    }
  }

  return (
    <Chip
      label={label}
      color={color}
      size="small"
      sx={{
        textTransform: 'capitalize',
        fontWeight: 600,
        minWidth: 120,
        ...(color === 'error' && { border: '1px solid #d32f2f', bgcolor: '#ffebee', color: '#c62828' }),
        ...((label === 'Grace Period' || label === 'Extended Return Window') && { 
          border: '1px solid #bee3f8', 
          bgcolor: '#ebf8ff', 
          color: '#2b6cb0' 
        })
      }}
    />
  );
};

const displayUserId = (id) => (id === '_no_unique_customer_424' ? 'BOH' : id);

const TransactionTableContent = ({ transactions, isAdmin, onRefundClick, onMarkReturnedClick, formatDate, defaultUserId }) => (
  <TableContainer>
    <Table size="small">
      <TableHead sx={{ bgcolor: '#f9fafb' }}>
        <TableRow>
          <HeaderCell>User ID</HeaderCell>
          <HeaderCell>Time</HeaderCell>
          <HeaderCell>Due Date</HeaderCell>
          <HeaderCell>Location</HeaderCell>
          <HeaderCell>Qty</HeaderCell>
          <HeaderCell>Status</HeaderCell>
          <HeaderCell>Returned At</HeaderCell>
          {isAdmin && <HeaderCell>Actions</HeaderCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {transactions?.map((tx, i) => {
          const txDate = tx.timestamp ? new Date(tx.timestamp) : null;
          const timeDisplay = (txDate && !isNaN(txDate)) 
            ? txDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
            : '-';

          const isLateFeeStatus = tx.late_fee_charged || tx.return_status?.toLowerCase() === 'late';
          const returnedAtDisplay = isLateFeeStatus ? '-' : formatDate(tx.returned_at_timestamp);

          const status = tx.return_status?.toLowerCase();
          const isUnreturned = status === 'not returned' || status === 'unreturned' || status === 'late' || status === 'grace period' || status === 'extended return window';

          return (
            <TableRow key={i} hover>
              <TableCell sx={{ fontFamily: 'monospace' }}>{displayUserId(tx.user_id || defaultUserId)}</TableCell>
              <TableCell>{timeDisplay}</TableCell>
              <TableCell>{formatDate(tx.return_by_timestamp)}</TableCell>
              <TableCell>{tx.device_id ? getLocationName(tx.device_id) : (tx.location_id ?? '-')}</TableCell>
              <TableCell>{tx.number_of_units}</TableCell>
              <TableCell>
                <StatusBadge 
                  status={tx.return_status} 
                  lateFeeCharged={tx.late_fee_charged} 
                  paymentStatus={tx.payment_status} 
                  refunded={tx.refunded} 
                />
              </TableCell>
              <TableCell>{returnedAtDisplay}</TableCell>
              {isAdmin && (
                <TableCell>
                  <Stack direction="column" spacing={0.5} sx={{ maxWidth: 110 }}>
                    {tx.late_fee_charged && !tx.refunded && (
                      <Button 
                        size="small" 
                        variant="outlined" 
                        color="success" 
                        onClick={() => onRefundClick(tx)} 
                        sx={{ fontSize: '0.65rem', fontWeight: 700, py: 0.2, textTransform: 'uppercase' }}
                      >
                        Refund
                      </Button>
                    )}
                    {isUnreturned && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={() => onMarkReturnedClick(tx)}
                        sx={{ fontSize: '0.65rem', fontWeight: 700, py: 0.2, textTransform: 'uppercase' }}
                      >
                        Mark Returned
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

const MetricCard = ({ title, value, icon, color = BRAND_GREEN }) => (
  <Card elevation={2} sx={{ height: '100%', background: `linear-gradient(135deg, ${BRAND_GREEN_LIGHT} 0%, #ffffff 100%)`, border: `1px solid ${BRAND_GREEN_BORDER}` }}>
    <CardContent>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: color, color: 'white', display: 'flex' }}>{icon}</Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>{title}</Typography>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: BRAND_GREEN_DARK }}>
            {value != null ? (typeof value === 'string' ? value : value.toLocaleString()) : 0}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const SectionLabel = ({ children }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
    <Typography variant="overline" sx={{ fontWeight: 400, fontSize: '1.1rem', color: '#1a202c', letterSpacing: 1.2 }}>
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', bgcolor: '#e2e8f0' }} />
  </Stack>
);

const AdminDashboard = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [data, setData] = useState(null);
  const [users, setUsers] = useState(null);
  const [lateData, setLateData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(todayISODate);
  const [endDate, setEndDate] = useState(todayISODate);
  const [expandedDates, setExpandedDates] = useState({});

  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [refundUnits, setRefundUnits] = useState('');

  const [markReturnedModalOpen, setMarkReturnedModalOpen] = useState(false);
  const [markReturnedTx, setMarkReturnedTx] = useState(null);
  const [markReturnedUnits, setMarkReturnedUnits] = useState('');

  useEffect(() => {
    const urlKey = searchParams.get('key');
    const storedSession = sessionStorage.getItem('dishlink_admin_session');
    const INTERNAL_KEY = process.env.REACT_APP_ADMIN_ACCESS_KEY;
    const CLIENT_KEY = process.env.REACT_APP_CLIENT_ACCESS_KEY;

    if (storedSession) {
      setIsAuthorized(true); setIsAdmin(storedSession === 'internal');
      fetchData(todayISODate(), todayISODate());
    } else if (urlKey === INTERNAL_KEY || urlKey === CLIENT_KEY) {
      const type = (urlKey === INTERNAL_KEY) ? 'internal' : 'client';
      sessionStorage.setItem('dishlink_admin_session', type);
      setIsAuthorized(true); setIsAdmin(type === 'internal');
      navigate('/admin', { replace: true });
      fetchData(todayISODate(), todayISODate());
    } else {
      navigate('/');
    }
  }, []);

  const fetchData = async (from = startDate, to = endDate) => {
    setLoading(true); setError(null);
    try {
      const token = await getAuthToken();
      const [tData, uData, lData] = await Promise.all([
        getTransactionsByDate(from, to, token),
        getUsersByDate(from, to, token),
        getLateTransactions(from, to, token)
      ]);
      setData(tData); setUsers(uData); setLateData(lData);
    } catch (err) { setError('Failed to load dashboard data.'); } finally { setLoading(false); }
  };

  const handleSearchInputChange = async (event, value) => {
    if (!value || value.length < 3) {
      setAutocompleteOptions([]);
      return;
    }
    try {
      const token = await getAuthToken();
      const response = await searchCustomers(value, token);
      const resultsArray = response.results || [];
      const formatted = resultsArray.map(obj => {
        const [id, phone] = Object.entries(obj)[0];
        return { label: `${displayUserId(id)} (${phone})`, value: id };
      });
      setAutocompleteOptions(formatted);
    } catch (err) { console.error("Search error:", err); }
  };

  const handleUserSelection = async (event, selection) => {
    if (!selection) return;
    setProfileLoading(true);
    try {
      const token = await getAuthToken();
      const result = await getTransactionsByDate('2020-01-01', todayISODate(), token, selection.value);
      const allTx = Object.values(result)
        .filter(val => typeof val === 'object' && val.transactions)
        .flatMap(d => d.transactions);

      setUserProfile({ user_id: selection.value, transactions: allTx });
    } catch (err) { alert('User history not found.'); } finally { setProfileLoading(false); }
  };

  const onRefundClick = (tx) => {
    if (tx.number_of_units > 1) {
      setSelectedTx(tx);
      setRefundUnits(tx.number_of_units);
      setRefundModalOpen(true);
    } else {
      if (window.confirm('Confirm refund for this transaction?')) {
        handleRefundSubmission(tx.transaction_id, 1);
      }
    }
  };

  const handleRefundSubmission = async (transactionId, units) => {
    try {
      const token = await getAuthToken();
      const unitsToSend = units === '' ? null : parseInt(units);
      await refundLateFee(transactionId, unitsToSend, token);

      setData(prevData => {
        const newData = { ...prevData };
        Object.keys(newData).forEach(date => {
          if (newData[date]?.transactions) {
            newData[date].transactions = newData[date].transactions.map(tx => 
              tx.transaction_id === transactionId ? { ...tx, refunded: true } : tx
            );
          }
        });
        return newData;
      });

      alert('Refund successful');
      setRefundModalOpen(false);
      fetchData();
      if (userProfile) {
        handleUserSelection(null, { value: userProfile.user_id });
      }
    } catch (err) { alert(err.message || 'Refund failed'); }
  };

  const onMarkReturnedClick = (tx) => {
    if (tx.number_of_units > 1) {
      setMarkReturnedTx(tx);
      setMarkReturnedUnits(tx.number_of_units);
      setMarkReturnedModalOpen(true);
    } else {
      if (window.confirm('Mark this transaction as returned?')) {
        handleMarkReturnedSubmission(tx.transaction_id, null);
      }
    }
  };

  const handleMarkReturnedSubmission = async (transactionId, units) => {
    try {
      const token = await getAuthToken();
      const unitsToSend = units === '' || units == null ? null : parseInt(units);
      await markTransactionReturned(transactionId, unitsToSend, token);

      setData(prevData => {
        const newData = { ...prevData };
        Object.keys(newData).forEach(date => {
          if (newData[date]?.transactions) {
            newData[date].transactions = newData[date].transactions.map(tx =>
              tx.transaction_id === transactionId ? { ...tx, return_status: 'Returned' } : tx
            );
          }
        });
        return newData;
      });

      alert('Transaction marked as returned');
      setMarkReturnedModalOpen(false);
      fetchData();
      if (userProfile) {
        handleUserSelection(null, { value: userProfile.user_id });
      }
    } catch (err) { alert(err.message || 'Failed to mark as returned'); }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return isNaN(date) ? '-' : date.toLocaleDateString();
  };

  // const formatDollars = (value) => {
  //   if (value == null) return '$0.00';
  //   const num = typeof value === 'number' ? value : parseFloat(value);
  //   return isNaN(num) ? '$0.00' : `$${num.toFixed(2)}`;
  // };

  const unitsReturnedInWindow = Object.entries(data || {})
    .filter(([key]) => /^\d{4}-\d{2}-\d{2}$/.test(key))
    .flatMap(([, day]) => day?.transactions || [])
    .filter(tx => tx.return_status?.toLowerCase() === 'returned')
    .reduce((sum, tx) => sum + (Number(tx.number_of_units) || 0), 0);

  if (!isAuthorized) return null;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#1a202c' }}>
          {isAdmin ? 'Dishlink Admin Dashboard (Internal)' : 'Client Impact Portal'}
        </Typography>
      </Box>

      <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 4, backgroundColor: '#f8f9fa' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ backgroundColor: 'white', '& input': { fontSize: '1rem', fontWeight: 400 } }} />
          <TextField label="End Date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ backgroundColor: 'white', '& input': { fontSize: '1rem', fontWeight: 400 } }} />
          <Button variant="contained" onClick={() => fetchData()} sx={{ height: 40, px: 3, backgroundColor: BRAND_GREEN, '&:hover': { backgroundColor: BRAND_GREEN_DARK } }} startIcon={<RefreshIcon />}>Refresh</Button>

          {isAdmin && (
            <Autocomplete
              sx={{ minWidth: 350, ml: 'auto', bgcolor: 'white' }}
              options={autocompleteOptions}
              onInputChange={handleSearchInputChange}
              onChange={handleUserSelection}
              loading={profileLoading}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField {...params} size="small" placeholder="Find Phone or User ID..." InputProps={{ ...params.InputProps, startAdornment: (<PersonSearchIcon sx={{ ml: 1, color: 'action.active' }} />) }} />
              )}
            />
          )}
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {data && (
        <>
          <SectionLabel>All-Time Totals</SectionLabel>
          <Grid container spacing={3} sx={{ mb: 2 }}>
            {/* <Grid item xs={12} md={3}><MetricCard title="Total Users" value={users?.total_users_all_time} icon={<GroupAddIcon />} /></Grid> */}
            <Grid item xs={12} md={6}><MetricCard title="Total Reusables" value={data?.total_units_all_time} icon={<InventoryIcon />} /></Grid>
            <Grid item xs={12} md={6}><MetricCard title="Reusables Currently Out" value={data?.total_units_currently_checked_out_all_time} icon={<ShoppingCartCheckoutIcon />} /></Grid>
            {/* <Grid item xs={12} md={3}><MetricCard title="Total Units Charged Late Fee" value={lateData?.total_units_all_time} icon={<ReceiptLongIcon />} /></Grid> */}
          </Grid>

          <SectionLabel>
            Selected Window ({startDate}{startDate !== endDate ? ` → ${endDate}` : ''})
          </SectionLabel>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <MetricCard
                title="Reusables Returned"
                value={unitsReturnedInWindow}
                icon={<AssignmentReturnIcon />}
              />
            </Grid>
            {/*
            <Grid item xs={12} md={6}>
              <MetricCard
                title="New Checkouts"
                value={data?.units_in_time_window}
                icon={<DateRangeIcon />}
              />
            </Grid>
            */}
            {/* {isAdmin && (
              <>
                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="Units Charged Late Fee"
                    value={lateData?.units_in_time_window}
                    icon={<ReceiptLongIcon />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="Late Fee Revenue"
                    value={formatDollars(lateData?.late_fee_in_time_window)}
                    icon={<AttachMoneyIcon />}
                  />
                </Grid>
              </>
            )} */}
          </Grid>

          <GracePeriodNote />
          <Box sx={{ mt: 2 }}>
            {Object.entries(data).filter(([key]) => /^\d{4}-\d{2}-\d{2}$/.test(key)).sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime()).map(([date, dayData]) => (
              <Accordion key={date} expanded={!!expandedDates[date]} onChange={(e, isExp) => setExpandedDates(p => ({ ...p, [date]: isExp }))} disableGutters elevation={1} sx={{ mb: 1, border: '1px solid #e0e0e0', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: !!expandedDates[date] ? '#f5faff' : '#fff', borderRadius: '8px' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" spacing={2} sx={{ pr: 2 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.25rem', fontWeight: 400 }}>{date}</Typography>
                    <Stack direction="row" spacing={3} alignItems="center">
                      {lateData?.[date]?.total_units > 0 && <Chip label={`${lateData[date].total_units} Late`} size="small" sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700 }} />}
                      <Chip icon={<GroupAddIcon style={{ fontSize: 16, color: BRAND_GREEN_DARK }} />} label={`New: ${users?.[date]?.total_users || 0}`} variant="outlined" size="small" sx={{ fontWeight: 600, border: `1px solid ${BRAND_GREEN_BORDER}`, bgcolor: BRAND_GREEN_LIGHT, color: BRAND_GREEN_DARK, '& .MuiChip-icon': { color: BRAND_GREEN_DARK } }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Units: {dayData.total_units}</Typography>
                    </Stack>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <TransactionTableContent transactions={dayData.transactions} isAdmin={isAdmin} onRefundClick={onRefundClick} onMarkReturnedClick={onMarkReturnedClick} formatDate={formatDate} />
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
          {!isAdmin && <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', mt: 4 }}><AssessmentIcon sx={{ fontSize: 48, color: BRAND_GREEN, mb: 2 }} /><Typography variant="h6">Impact Summary</Typography><Typography color="text.secondary">Your organization has diverted significant waste through the Dishlink program.</Typography></Paper>}
        </>
      )}

      <Dialog open={!!userProfile} onClose={() => setUserProfile(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>User Transaction History: {displayUserId(userProfile?.user_id)}</DialogTitle>
        <DialogContent dividers>
          {userProfile?.transactions?.length > 0 ? (
            <TransactionTableContent 
               transactions={userProfile.transactions} 
               isAdmin={isAdmin} 
               onRefundClick={onRefundClick}
               onMarkReturnedClick={onMarkReturnedClick}
               formatDate={formatDate} 
               defaultUserId={userProfile.user_id}
            />
          ) : <Typography sx={{ py: 2, textAlign: 'center' }}>No transaction history found for this search.</Typography>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}><Button variant="outlined" onClick={() => setUserProfile(null)}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={refundModalOpen} onClose={() => setRefundModalOpen(false)}>
        <DialogTitle>Partial Refund</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>Refund how many of <strong>{selectedTx?.number_of_units}</strong> units?</Typography>
          <TextField fullWidth type="number" value={refundUnits} onChange={(e) => setRefundUnits(e.target.value)} helperText="Blank = all" autoFocus />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRefundModalOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={() => handleRefundSubmission(selectedTx.transaction_id, refundUnits)}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={markReturnedModalOpen} onClose={() => setMarkReturnedModalOpen(false)}>
        <DialogTitle>Mark as Returned</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>Mark how many of <strong>{markReturnedTx?.number_of_units}</strong> units as returned?</Typography>
          <TextField fullWidth type="number" value={markReturnedUnits} onChange={(e) => setMarkReturnedUnits(e.target.value)} helperText="Blank = all" autoFocus />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMarkReturnedModalOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={() => handleMarkReturnedSubmission(markReturnedTx.transaction_id, markReturnedUnits)}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;